import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { startGebruiksEvents } from '../lib/gebruiksEvents';
import { invalidatePermissionsCache } from '../lib/permissions';
import { clearTokenCache } from '../lib/edgeFunctions';
import { setSentryUser } from '../lib/sentry';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  signature_url: string | null;
  function_title: string | null;
  phone: string | null;
  role:
    | 'Admin'
    | 'Projectleider'
    | 'Werkvoorbereider'
    | 'Onderaannemer'
    // Medewerker van een onderaannemer (#378). Stond hier niet, waardoor het
    // type beweerde dat deze rol niet kon bestaan -- en 'bekijken als' hem niet
    // kon aanbieden.
    | 'OA-medewerker'
    | 'Administratie'
    | 'Planner'
    | 'Adviseur'
    | 'Hoofd Adviseur'
    | 'Secretaresse'
    | 'Transporteur';
  contact_id: string | null;
  created_at: string;
}

export type Role = Profile['role'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  /** Effectief profiel: bij een actieve weergave-rol is `role` overschreven. */
  profile: Profile | null;
  /** Het échte profiel (altijd de DB-rol) — voor de weergave-schakelaar zelf. */
  realProfile: Profile | null;
  /** Actieve weergave-rol (alleen mogelijk als de echte rol Admin is), anders null. */
  viewAsRole: Role | null;
  /** Zet/wis de weergave-rol. Alleen effectief voor echte Admins; puur weergave (RLS/auth wijzigt niet). */
  setViewAsRole: (role: Role | null) => void;
  loading: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** True als de ingelogde gebruiker een geverifieerde MFA-factor heeft maar de sessie nog aal1 is. */
  mfaRequired: boolean;
  /** Voltooi de MFA-login-uitdaging met de 6-cijferige code. */
  verifyMfaChallenge: (code: string) => Promise<void>;
}

const VIEW_AS_KEY = 'pracht_view_as_role';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PROFILE_RETRIES = 2;
const PROFILE_RETRY_DELAY_MS = 1000;
const SESSION_REFRESH_RETRIES = 3;
const SESSION_REFRESH_DELAY_MS = 1500;

function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  const msg = String(e.message || '').toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed') ||
    msg.includes('networkerror') ||
    e.status === 0
  );
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  for (let attempt = 0; attempt <= PROFILE_RETRIES; attempt++) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) return data;
      if (error) console.error('Error loading profile:', error);
    } catch (err) {
      console.error('Profile load exception:', err);
    }
    if (attempt < PROFILE_RETRIES) {
      await new Promise((r) => setTimeout(r, PROFILE_RETRY_DELAY_MS * (attempt + 1)));
    }
  }
  return null;
}

async function tryRefreshSession(): Promise<Session | null> {
  for (let attempt = 0; attempt < SESSION_REFRESH_RETRIES; attempt++) {
    try {
      const {
        data: { session: refreshed },
        error,
      } = await supabase.auth.refreshSession();
      if (refreshed?.user) return refreshed;
      if (error && !isNetworkError(error)) return null;
    } catch (err) {
      if (!isNetworkError(err)) return null;
    }
    if (attempt < SESSION_REFRESH_RETRIES - 1) {
      await new Promise((r) => setTimeout(r, SESSION_REFRESH_DELAY_MS * (attempt + 1)));
    }
  }
  return null;
}

/**
 * Atomically resolve a session into a full auth state (user + profile).
 * Returns null if the session cannot be fully resolved.
 */
async function resolveAuth(
  sess: Session,
): Promise<{ user: User; session: Session; profile: Profile } | null> {
  // Start profile fetch in parallel with user validation
  const profilePromise = fetchProfile(sess.user.id);

  let activeSession = sess;
  let activeUser: User | null = null;

  try {
    const {
      data: { user: validated },
      error,
    } = await supabase.auth.getUser();

    if (error || !validated) {
      if (isNetworkError(error)) {
        activeUser = sess.user;
      } else if (sess.refresh_token) {
        const refreshed = await tryRefreshSession();
        if (!refreshed) return null;
        activeSession = refreshed;
        activeUser = refreshed.user;
      } else {
        return null;
      }
    } else {
      activeUser = validated;
    }
  } catch {
    activeUser = sess.user;
  }

  if (!activeUser) return null;

  const profile = await profilePromise;
  if (!profile) return null;

  return { user: activeUser, session: activeSession, profile };
}

function zelfdeInhoud<T>(a: T | null, b: T): boolean {
  return a !== null && JSON.stringify(a) === JSON.stringify(b);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [viewAsRole, setViewAsRoleState] = useState<Role | null>(() => {
    try {
      return (sessionStorage.getItem(VIEW_AS_KEY) as Role) || null;
    } catch {
      return null;
    }
  });

  const setViewAsRole = useCallback((role: Role | null) => {
    try {
      if (role) sessionStorage.setItem(VIEW_AS_KEY, role);
      else sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* sessionStorage niet beschikbaar */
    }
    invalidatePermissionsCache();
    setViewAsRoleState(role);
  }, []);

  // Deferred auth event: the onAuthStateChange callback writes here
  // synchronously, and a separate useEffect processes it outside the
  // supabase-js internal auth lock (prevents deadlock).
  const [deferredEvent, setDeferredEvent] = useState<{
    event: AuthChangeEvent;
    session: Session | null;
    seq: number;
  } | null>(null);
  const deferSeqRef = useRef(0);

  const initializingRef = useRef(true);
  const cancelledRef = useRef(false);
  const pendingEventRef = useRef<{ event: AuthChangeEvent; session: Session | null } | null>(null);
  // Generation counter: incremented on every new auth attempt. Stale
  // resolveAuth completions whose generation doesn't match are discarded.
  const authGenRef = useRef(0);

  const setFullAuth = useCallback((u: User, s: Session, p: Profile) => {
    // Guard: profile must belong to the same user as the session
    if (p.id !== u.id) return;
    // Zelfde inhoud → zelfde object houden. Anders draaien bij elke
    // terugkeer naar het tabblad alle effecten op user/profile opnieuw.
    setUser((prev) => (zelfdeInhoud(prev, u) ? prev : u));
    setSession((prev) => (prev?.access_token === s.access_token ? prev : s));
    setProfile((prev) => (zelfdeInhoud(prev, p) ? prev : p));
    setSentryUser({ id: p.id, email: p.email, role: p.role });
    // Gebruiksanalyse (besluit Tom 29-08): alleen interne rollen.
    if (!['Onderaannemer', 'OA-medewerker', 'Transporteur'].includes(p.role ?? '')) {
      startGebruiksEvents({ id: p.id, rol: p.role ?? null });
    }
  }, []);

  const clearAuth = useCallback(() => {
    authGenRef.current++;
    setUser(null);
    setSession(null);
    setProfile(null);
    setSentryUser(null);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    initializingRef.current = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (cancelledRef.current) return;

      if (event === 'SIGNED_OUT') {
        invalidatePermissionsCache();
        clearTokenCache();
        clearAuth();
        setLoading(false);
        return;
      }

      if (initializingRef.current) {
        pendingEventRef.current = { event, session: newSession };
        return;
      }

      // Defer processing to a useEffect so Supabase calls run outside
      // the internal auth lock (avoids deadlock in supabase-js >=2.46).
      setDeferredEvent({ event, session: newSession, seq: ++deferSeqRef.current });
    });

    async function initSession() {
      const gen = ++authGenRef.current;
      try {
        const {
          data: { session: s },
        } = await supabase.auth.getSession();
        if (cancelledRef.current || gen !== authGenRef.current) return;

        if (!s) {
          clearAuth();
          setLoading(false);
          return;
        }

        const resolved = await resolveAuth(s);
        if (cancelledRef.current || gen !== authGenRef.current) return;

        if (resolved) {
          setFullAuth(resolved.user, resolved.session, resolved.profile);
        } else {
          clearAuth();
          await supabase.auth.signOut().catch(() => {});
        }

        setLoading(false);
        initializingRef.current = false;

        // Process any auth event that arrived during initialization
        const pending = pendingEventRef.current;
        pendingEventRef.current = null;
        if (pending && pending.event !== 'SIGNED_OUT' && pending.session?.user) {
          const pendingGen = ++authGenRef.current;
          const pendingResolved = await resolveAuth(pending.session);
          if (!cancelledRef.current && pendingGen === authGenRef.current && pendingResolved) {
            setFullAuth(pendingResolved.user, pendingResolved.session, pendingResolved.profile);
          }
        }
      } catch {
        if (!cancelledRef.current) clearAuth();
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
          initializingRef.current = false;
        }
      }
    }

    initSession();

    return () => {
      cancelledRef.current = true;
      subscription.unsubscribe();
    };
  }, [clearAuth, setFullAuth]);

  // Process deferred auth events outside the supabase-js auth lock.
  // The onAuthStateChange callback only writes to deferredEvent; all
  // async Supabase calls (fetchProfile, resolveAuth, getUser, etc.)
  // happen here where no lock is held.
  useEffect(() => {
    if (!deferredEvent || !deferredEvent.session?.user) return;
    const { event, session: newSession } = deferredEvent;
    let cancelled = false;

    (async () => {
      const gen = ++authGenRef.current;

      if (event === 'TOKEN_REFRESHED') {
        const freshProfile = await fetchProfile(newSession!.user.id);
        if (cancelled || gen !== authGenRef.current) return;
        if (freshProfile) {
          setFullAuth(newSession!.user, newSession!, freshProfile);
        }
        setLoading(false);
        return;
      }

      setAuthError(null);
      let resolved = await resolveAuth(newSession!);
      if (cancelled || gen !== authGenRef.current) return;

      if (!resolved && event === 'SIGNED_IN') {
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled || gen !== authGenRef.current) return;
        resolved = await resolveAuth(newSession!);
        if (cancelled || gen !== authGenRef.current) return;
      }

      if (resolved) {
        setFullAuth(resolved.user, resolved.session, resolved.profile);
      } else if (event === 'SIGNED_IN') {
        setAuthError('Inloggen mislukt: je profiel kon niet worden geladen. Probeer het opnieuw.');
        clearAuth();
        await supabase.auth.signOut().catch(() => {});
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [deferredEvent, clearAuth, setFullAuth]);

  const loadProfile = useCallback(async (userId: string): Promise<boolean> => {
    const p = await fetchProfile(userId);
    if (p) {
      setProfile(p);
      return true;
    }
    return false;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    invalidatePermissionsCache();
    clearTokenCache();
    try {
      sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* ignore */
    }
    setViewAsRoleState(null);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  }, []);

  // Geïsoleerde MFA-gate: heeft de gebruiker een geverifieerde factor maar staat de sessie
  // nog op aal1? Dan is een login-uitdaging nodig. Fail-open: bij een fout NOOIT blokkeren
  // (voorkomt lockout). Gebruikers zonder factor krijgen nextLevel 'aal1' -> geen uitdaging.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setMfaRequired(false);
      return;
    }
    supabase.auth.mfa
      .getAuthenticatorAssuranceLevel()
      .then(({ data }) => {
        if (cancelled) return;
        setMfaRequired(data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2');
      })
      .catch(() => {
        if (!cancelled) setMfaRequired(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const verifyMfaChallenge = useCallback(async (code: string) => {
    const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
    if (listError) throw listError;
    const totp = factors?.totp?.find((f) => f.status === 'verified');
    if (!totp) {
      setMfaRequired(false);
      return;
    }
    const { data: ch, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: totp.id,
    });
    if (challengeError) throw challengeError;
    const { error } = await supabase.auth.mfa.verify({
      factorId: totp.id,
      challengeId: ch.id,
      code: code.trim(),
    });
    if (error) throw error;
    setMfaRequired(false);
  }, []);

  // Re-validate session AND profile when the app resumes after being backgrounded.
  // Without this, Android/PWA users get stuck with an expired JWT or stale profile.
  useEffect(() => {
    let lastHiddenAt = 0;

    const handler = async () => {
      if (document.hidden) {
        lastHiddenAt = Date.now();
        return;
      }
      if (!lastHiddenAt || Date.now() - lastHiddenAt < 5_000) return;
      if (!user) return;

      const gen = ++authGenRef.current;

      try {
        const {
          data: { session: s },
        } = await supabase.auth.getSession();
        if (gen !== authGenRef.current) return;
        if (!s) {
          invalidatePermissionsCache();
          clearTokenCache();
          clearAuth();
          return;
        }
        // Reload profile to ensure role matches the current session
        const freshProfile = await fetchProfile(s.user.id);
        if (gen !== authGenRef.current) return;
        if (freshProfile) {
          setFullAuth(s.user, s, freshProfile);
        }
      } catch {
        // Network error on resume — keep current state
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [user, clearAuth, setFullAuth]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadProfile(user.id);
    }
  }, [user, loadProfile]);

  // Weergave-rol: ALLEEN wanneer de echte DB-rol Admin is. Puur presentatie —
  // de sessie/RLS blijft de admin-gebruiker; dit versmalt alleen wat de UI toont.
  const activeViewAs =
    profile?.role === 'Admin' && viewAsRole && viewAsRole !== 'Admin' ? viewAsRole : null;
  const effectiveProfile = activeViewAs && profile ? { ...profile, role: activeViewAs } : profile;

  const value = {
    user,
    session,
    profile: effectiveProfile,
    realProfile: profile,
    viewAsRole: activeViewAs,
    setViewAsRole,
    loading,
    authError,
    signIn,
    signOut,
    refreshProfile,
    mfaRequired,
    verifyMfaChallenge,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const AUTH_FALLBACK: AuthContextType = {
  user: null,
  session: null,
  profile: null,
  realProfile: null,
  viewAsRole: null,
  setViewAsRole: () => {},
  loading: true,
  authError: null,
  signIn: async () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
  mfaRequired: false,
  verifyMfaChallenge: async () => {},
};

let lastKnownContext: AuthContextType | null = null;

export function useAuth() {
  const context = useContext(AuthContext);
  if (context !== undefined) {
    lastKnownContext = context;
    return context;
  }
  if (lastKnownContext) {
    return lastKnownContext;
  }
  return AUTH_FALLBACK;
}
